import { SkeletonSet } from '@cwos/ui-admin';
import { SettingsActions, SettingsCard, SettingsField } from '../settings/common.js';
import {
  BREADCRUMB_COLLECTION_WHEN,
  CLOCK_COLLECTION_WHEN,
  EVV_SEPARATION_SENTENCE,
  ORGANIZATION_AUDIENCE,
  PURGE_JOB_NAME,
  PURGE_SWEEP_SENTENCE,
} from './copy.js';
import { LocationSettingsShell } from './LocationSettingsShell.js';
import { PolicySchedule } from './PolicySchedule.js';
import { usePolicyForm, validateDraft } from './policyForm.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Privacy & retention — the plain-language page (directive §13): what is
 * collected and when, who can see it, how long it is kept, and the fact that
 * deletion is enforced by the REAL purge sweep (`system.breadcrumb-purge`),
 * not a promise — at the cadence that sweep actually runs, which is hourly and
 * was described here as nightly until F458. Also states the workforce-vs-EVV
 * separation the design doc calls load-bearing.
 */
export function LocationPrivacyPage() {
  const form = usePolicyForm(null);

  return (
    <LocationSettingsShell active="privacy" dirty={form.dirty}>
      {form.loading ? (
        <SettingsCard>
          <SkeletonSet surface="table" rows={4} />
        </SettingsCard>
      ) : form.loadError || !form.draft || !form.policy ? (
        <SettingsCard>
          <LoadFailure
            error={form.loadFailure}
            message="We couldn’t load the policy just now."
            onRetry={form.refetch}
          />
        </SettingsCard>
      ) : (
        <>
          <SettingsCard>
            <h2 className="rn-card-title">What is collected, and when</h2>
            <ul
              className="rn-muted"
              style={{
                marginTop: 'var(--space-12)',
                paddingLeft: 'var(--space-16)',
                display: 'grid',
                gap: 'var(--space-6)',
              }}
            >
              <li>
                A location at the clock punch — {CLOCK_COLLECTION_WHEN.toLowerCase()}.{' '}
                {form.policy.mode === 'OFF' ? 'Currently off for your organization.' : ''}
              </li>
              <li>
                Location points during shifts — {BREADCRUMB_COLLECTION_WHEN.toLowerCase()}.{' '}
                {form.policy.breadcrumbEnabled
                  ? 'Currently on for your organization.'
                  : 'Currently off for your organization.'}
              </li>
              <li>Never while a worker is clocked out. There is no off-shift tracking.</li>
            </ul>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">Who can see it</h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
              People your organization has granted location access can see punch results and shift
              coverage, and every worker can always see their own. Opening a raw movement trail
              takes a separate, rarely-granted permission, and every viewing is recorded against the
              viewer’s name.
            </p>
            <details style={{ marginTop: 'var(--space-8)' }}>
              <summary className="rn-muted" style={{ cursor: 'pointer', fontSize: 13 }}>
                Advanced details
              </summary>
              <p className="rn-muted" style={{ fontSize: 13, marginTop: 'var(--space-4)' }}>
                Reads are gated by <code>location:read</code>; movement trails additionally require{' '}
                <code>location_history:view</code>, which no role holds by default — it is granted
                person by person, and each trail view writes an audit event.
              </p>
            </details>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">How long it is kept</h2>
            <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
              <SettingsField
                label="Days location points are kept"
                hint="Between 1 and 3,650 days. Then they are permanently deleted."
                error={validateDraft(form.draft).breadcrumbRetentionDays}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.draft.breadcrumbRetentionDays}
                  disabled={!form.canManage}
                  onChange={(e) => form.set({ breadcrumbRetentionDays: e.target.value })}
                  style={{ maxWidth: 120 }}
                />
              </SettingsField>
              <p className="rn-muted" style={{ fontSize: 13 }}>
                {PURGE_SWEEP_SENTENCE}
              </p>
              <details>
                <summary className="rn-muted" style={{ cursor: 'pointer', fontSize: 13 }}>
                  Advanced details
                </summary>
                <p className="rn-muted" style={{ fontSize: 13, marginTop: 'var(--space-4)' }}>
                  The sweep runs as the system job <code>{PURGE_JOB_NAME}</code>. A program policy
                  with its own retention window is purged on that window.
                </p>
              </details>
            </div>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">Separate from EVV</h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
              {EVV_SEPARATION_SENTENCE}
            </p>
          </SettingsCard>

          {/* Every page that saves states when the save begins — see
              PolicySchedule's header: this page sends the whole policy body,
              so it makes a schedule decision on every save whether it shows
              one or not. */}
          <SettingsCard>
            <PolicySchedule form={form} audience={ORGANIZATION_AUDIENCE} />
          </SettingsCard>

          {form.canManage ? (
            <SettingsCard>
              <SettingsActions
                dirty={form.dirty}
                valid={form.valid}
                saving={form.saving}
                saved={form.saved}
                error={form.saveError}
                onSave={form.save}
                onCancel={form.cancel}
              />
            </SettingsCard>
          ) : null}
        </>
      )}
    </LocationSettingsShell>
  );
}
